'use client';

import { useState, useCallback } from 'react';
import { ToastItem } from '../components/ui/ToastContainer';

const useToast = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prevToasts) => [...prevToasts, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback((titleOrMessage: string, messageOrDuration?: string | number, duration = 2000) => {
    // Handle optional title
    if (typeof messageOrDuration === 'number' || messageOrDuration === undefined) {
      // If second param is a number or undefined, then first param is the message
      const actualDuration = typeof messageOrDuration === 'number' ? messageOrDuration : duration;
      return addToast({ message: titleOrMessage, type: 'success', duration: actualDuration });
    } else {
      // Otherwise, first param is title, second is message
      return addToast({ title: titleOrMessage, message: messageOrDuration, type: 'success', duration });
    }
  }, [addToast]);

  const error = useCallback((titleOrMessage: string, messageOrDuration?: string | number, duration = 2000) => {
    // Handle optional title
    if (typeof messageOrDuration === 'number' || messageOrDuration === undefined) {
      // If second param is a number or undefined, then first param is the message
      const actualDuration = typeof messageOrDuration === 'number' ? messageOrDuration : duration;
      return addToast({ message: titleOrMessage, type: 'error', duration: actualDuration });
    } else {
      // Otherwise, first param is title, second is message
      return addToast({ title: titleOrMessage, message: messageOrDuration, type: 'error', duration });
    }
  }, [addToast]);

  const info = useCallback((titleOrMessage: string, messageOrDuration?: string | number, duration = 2000) => {
    // Handle optional title
    if (typeof messageOrDuration === 'number' || messageOrDuration === undefined) {
      // If second param is a number or undefined, then first param is the message
      const actualDuration = typeof messageOrDuration === 'number' ? messageOrDuration : duration;
      return addToast({ message: titleOrMessage, type: 'info', duration: actualDuration });
    } else {
      // Otherwise, first param is title, second is message
      return addToast({ title: titleOrMessage, message: messageOrDuration, type: 'info', duration });
    }
  }, [addToast]);

  const warning = useCallback((titleOrMessage: string, messageOrDuration?: string | number, duration = 2000) => {
    // Handle optional title
    if (typeof messageOrDuration === 'number' || messageOrDuration === undefined) {
      // If second param is a number or undefined, then first param is the message
      const actualDuration = typeof messageOrDuration === 'number' ? messageOrDuration : duration;
      return addToast({ message: titleOrMessage, type: 'warning', duration: actualDuration });
    } else {
      // Otherwise, first param is title, second is message
      return addToast({ title: titleOrMessage, message: messageOrDuration, type: 'warning', duration });
    }
  }, [addToast]);

  const toast = {
    success,
    error,
    info,
    warning,
  };

  return {
    toasts,
    addToast,
    removeToast,
    toast,
    // Keep the old methods for backward compatibility
    showSuccessToast: success,
    showErrorToast: error,
    showInfoToast: info,
    showWarningToast: warning,
  };
};

export default useToast;